// HeyMotek — Login Page
//
// Brand sheet:  ~/heymotek/brand/design-system.md
// Voice doc:    ~/heymotek/brand/voice.md
// Sister page:  SignupPage.tsx (signature register; this one stays lean)
//
// Login is a working surface, not a signature moment. Returning users
// know the product; the page just gets them in. No proof column, no
// backdrop, no selling — just utility wrapped in the brand voice.
//
// Auth: Firebase Google + email/password (matches SignupPage). No backend
// /signup/create-account call here — login is pure auth. If somehow a
// user has no app record, RequireClient routes them to onboarding.

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { auth } from '@/lib/firebase';

// ─────────────────────────────────────────────────────────────────────
// Schemas
// ─────────────────────────────────────────────────────────────────────

const signInSchema = z.object({
  email: z.email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
type SignInFormData = z.infer<typeof signInSchema>;

const resetSchema = z.object({
  email: z.email('Please enter a valid email'),
});
type ResetFormData = z.infer<typeof resetSchema>;

// ─────────────────────────────────────────────────────────────────────
// Firebase error mapping (brand voice)
// ─────────────────────────────────────────────────────────────────────

const firebaseErrorMessages: Record<string, string> = {
  // Sign-in errors
  'auth/invalid-credential': "Email or password don't match. Try again or reset your password.",
  'auth/user-not-found': 'No account with that email. Want to sign up instead?',
  'auth/wrong-password': "Email or password don't match. Try again or reset your password.",
  'auth/too-many-requests': 'Too many attempts. Wait a moment and try again.',
  'auth/user-disabled': "This account has been disabled. Contact support if that's a mistake.",
  // Shared
  'auth/invalid-email': "That doesn't look like a valid email.",
  'auth/network-request-failed': "Couldn't reach the server. Check your connection and try again.",
  'auth/popup-closed-by-user': 'Google sign-in was cancelled. Try again?',
  'auth/popup-blocked': 'Your browser blocked the Google sign-in popup. Allow popups and try again.',
};

function getErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code;
  if (code && firebaseErrorMessages[code]) return firebaseErrorMessages[code];
  const message = (err as { message?: string })?.message;
  return message || 'Something went wrong. Try again in a moment.';
}

// ─────────────────────────────────────────────────────────────────────
// Wordmark (CSS text-stroke — matches SignupPage)
// ─────────────────────────────────────────────────────────────────────

function HeymotekWordmark() {
  return (
    <span className="font-extrabold text-2xl tracking-tight">
      <span style={{ WebkitTextStroke: '2px #0F172A', color: 'white', paintOrder: 'stroke fill' }}>
        hey
      </span>
      <span className="text-brand">motek</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────

const googleProvider = new GoogleAuthProvider();
type Mode = 'sign-in' | 'reset' | 'reset-sent';

export function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('sign-in');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [authError, setAuthError] = useState<string>('');

  // If already signed in when landing here, bounce away.
  useEffect(() => {
    if (auth.currentUser) navigate('/', { replace: true });
  }, [navigate]);

  // ── Sign-in form ──
  const signInForm = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  async function handleGoogleSignIn() {
    setAuthError('');
    setGoogleLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      navigate('/', { replace: true });
    } catch (err) {
      setAuthError(getErrorMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleEmailSignIn(data: SignInFormData) {
    setAuthError('');
    try {
      await signInWithEmailAndPassword(auth, data.email, data.password);
      navigate('/', { replace: true });
    } catch (err) {
      setAuthError(getErrorMessage(err));
    }
  }

  // ── Reset form ──
  const resetForm = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    defaultValues: { email: '' },
  });

  async function handleSendReset(data: ResetFormData) {
    setAuthError('');
    try {
      await sendPasswordResetEmail(auth, data.email);
      setMode('reset-sent');
    } catch (err) {
      setAuthError(getErrorMessage(err));
    }
  }

  function switchMode(next: Mode) {
    setAuthError('');
    setMode(next);
  }

  const submitting = googleLoading || signInForm.formState.isSubmitting;

  return (
    <main className="min-h-screen flex flex-col items-center justify-center bg-bg-tint p-4 md:p-6">
      {/* Wordmark above the card */}
      <div className="mb-6">
        <HeymotekWordmark />
      </div>

      {/* Card */}
      <div className="w-full max-w-md bg-card rounded-2xl shadow-card border border-dark/5 p-8">
        {/* Header — same in all modes for predictability */}
        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-center mb-6">
          {mode === 'reset-sent' ? 'Check your email' : 'Sign in'}
        </h1>

        {/* ── Mode: sign-in ── */}
        {mode === 'sign-in' && (
          <>
            {/* Google */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base mb-5"
              onClick={handleGoogleSignIn}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden className="mr-2">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
                </svg>
              )}
              Sign in with Google
            </Button>

            {/* OR divider */}
            <div className="flex items-center gap-4 mb-3 text-muted-foreground text-xs uppercase tracking-wider font-semibold">
              <div className="flex-1 h-px bg-dark/10" />
              <span>OR</span>
              <div className="flex-1 h-px bg-dark/10" />
            </div>

            {/* Email/password form */}
            <form onSubmit={signInForm.handleSubmit(handleEmailSignIn)} className="space-y-3 mt-3" noValidate>
              <div>
                <Label htmlFor="signin-email" className="sr-only">Email</Label>
                <Input
                  id="signin-email"
                  type="email"
                  autoComplete="email"
                  placeholder="Your email"
                  className="h-12 rounded-xl"
                  {...signInForm.register('email')}
                  aria-invalid={!!signInForm.formState.errors.email}
                  aria-describedby={signInForm.formState.errors.email ? 'signin-email-error' : undefined}
                />
                {signInForm.formState.errors.email && (
                  <p id="signin-email-error" role="alert" className="mt-1 text-xs text-destructive">
                    {signInForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="signin-password" className="sr-only">Password</Label>
                <PasswordInput
                  id="signin-password"
                  autoComplete="current-password"
                  placeholder="Your password"
                  className="h-12 rounded-xl"
                  {...signInForm.register('password')}
                  aria-invalid={!!signInForm.formState.errors.password}
                  aria-describedby={signInForm.formState.errors.password ? 'signin-password-error' : undefined}
                />
                {signInForm.formState.errors.password && (
                  <p id="signin-password-error" role="alert" className="mt-1 text-xs text-destructive">
                    {signInForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {authError && (
                <p role="alert" aria-live="polite" className="text-sm text-destructive">
                  {authError}
                </p>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-base font-bold mt-2 shadow-card"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

            {/* Footer links */}
            <div className="mt-6 text-center text-sm text-muted-foreground space-y-2">
              <p>
                No account yet?{' '}
                <Link to="/signup" className="font-bold text-foreground hover:text-brand">
                  Sign up here.
                </Link>
              </p>
              <p>
                Forgot your password?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('reset')}
                  className="font-bold text-foreground hover:text-brand"
                >
                  Reset it.
                </button>
              </p>
            </div>
          </>
        )}

        {/* ── Mode: reset ── */}
        {mode === 'reset' && (
          <>
            <p className="text-sm text-muted-foreground text-center mb-5">
              Enter your email and we'll send you a link to reset your password.
            </p>

            <form onSubmit={resetForm.handleSubmit(handleSendReset)} className="space-y-3" noValidate>
              <div>
                <Label htmlFor="reset-email" className="sr-only">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  autoComplete="email"
                  placeholder="Your email"
                  className="h-12 rounded-xl"
                  {...resetForm.register('email')}
                  aria-invalid={!!resetForm.formState.errors.email}
                  aria-describedby={resetForm.formState.errors.email ? 'reset-email-error' : undefined}
                />
                {resetForm.formState.errors.email && (
                  <p id="reset-email-error" role="alert" className="mt-1 text-xs text-destructive">
                    {resetForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              {authError && (
                <p role="alert" aria-live="polite" className="text-sm text-destructive">
                  {authError}
                </p>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-base font-bold mt-2 shadow-card"
                disabled={resetForm.formState.isSubmitting}
              >
                {resetForm.formState.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    sending...
                  </>
                ) : (
                  'Send reset link'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <button
                type="button"
                onClick={() => switchMode('sign-in')}
                className="font-bold text-foreground hover:text-brand"
              >
                Back to sign in
              </button>
            </div>
          </>
        )}

        {/* ── Mode: reset-sent ── */}
        {mode === 'reset-sent' && (
          <>
            <p className="text-sm text-muted-foreground text-center mb-6">
              We sent a reset link to <span className="font-semibold text-foreground">{resetForm.getValues('email')}</span>.
              Click it to set a new password.
            </p>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base"
              onClick={() => switchMode('sign-in')}
            >
              Back to sign in
            </Button>
          </>
        )}
      </div>
    </main>
  );
}
